import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import { Button } from "@tracify/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import { Label } from "@tracify/ui/label";
import { useState, useEffect } from "react";
import { FacebookDataset } from "@lib/api-hooks/useFacebookPixel/useGetFacebookPixels";

interface DisconnectDatasetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (datasetId?: string) => void;
  datasetId?: string;
  datasets?: FacebookDataset[];
  isPending: boolean;
}

export function DisconnectDatasetModal({
  isOpen,
  onClose,
  onConfirm,
  datasetId,
  datasets = [],
  isPending,
}: DisconnectDatasetModalProps) {
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      if (datasets && datasets.length > 0) {
        setSelectedDatasetId(datasets[0].dataset_id);
      } else if (datasetId) {
        setSelectedDatasetId(datasetId);
      }
    }
  }, [isOpen, datasets, datasetId]);

  const hasMultiple = datasets && datasets.length > 1;

  const selectedDataset = datasets.find(
    (p) => p.dataset_id === (selectedDatasetId || datasetId)
  );
  const datasetDisplayName = selectedDataset
    ? `${selectedDataset.dataset_id}`
    : selectedDatasetId || datasetId || "";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Caution: You&apos;re about to disable conversion postback
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <p className="text-sm">
            If you continue, your dataset &quot;{datasetDisplayName}&quot; will
            no longer be receiving data from Tracify.
          </p>

          {hasMultiple && (
            <div className="space-y-2 mt-4">
              <Label>Select Dataset</Label>
              <Select
                value={selectedDatasetId}
                onValueChange={setSelectedDatasetId}
                disabled={isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a dataset" />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.dataset_id}>
                      Dataset ID: {dataset.dataset_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(selectedDatasetId)}
            loading={isPending}
            className="bg-red-500 hover:bg-red-600"
          >
            Disable conversion postback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
